import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import ReviewCertificate from "../ReviewCertificate";
import { trackPhoneCall, trackWhatsApp } from "../../utils/analytics";
import "./Footer.css";

const QUICK_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Blog" },
  { to: "/about-us", label: "About" },
  { to: "/contact-us", label: "Contact" },
  { to: "/quote", label: "Get a Quote" },
  { to: "/vip", label: "VIP+ Membership" },
  { to: "/device-health-check", label: "Free Device Health Check" },
  { to: "/business-accounts", label: "Business Accounts" },
];

const AREAS = [
  ["horsham", "Horsham, PA"],
  ["abington", "Abington, PA"],
  ["hatboro", "Hatboro, PA"],
  ["warminster", "Warminster, PA"],
  ["glenside", "Glenside, PA"],
  ["jenkintown", "Jenkintown, PA"],
];

const POLICIES = [
  ["/terms-conditions", "Terms & Conditions"],
  ["/privacy-policy", "Privacy Policy"],
  ["/return-refund-policy", "Returns & Refunds"],
  ["/warranty-policy", "Warranty Policy"],
];

const Footer = () => {
  const [nlStatus, setNlStatus] = useState({ type: "", message: "" });
  const [nlSubmitting, setNlSubmitting] = useState(false);
  const nlForm = useRef();

  // Mobile bottom-nav: collapse to a circle while scrolling, spring back when idle.
  const [navCollapsed, setNavCollapsed] = useState(false);
  // Play a smooth one-time entrance (slide-up + fade) after mount.
  const [navReady, setNavReady] = useState(false);
  useEffect(() => {
    const revealTimer = setTimeout(() => setNavReady(true), 350);

    let hideTimer;
    const onScroll = () => {
      setNavCollapsed(true);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setNavCollapsed(false), 650);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(revealTimer);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(hideTimer);
    };
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    setNlSubmitting(true);
    setNlStatus({ type: "", message: "" });

    const emailValue = nlForm.current.querySelector('input[name="email"]').value;

    emailjs
      .send(
        "service_f4grfuj",
        "template_bjg4ob4",
        {
          name: "Newsletter Subscriber",
          email: emailValue,
          reply_to: emailValue,
          device: "N/A",
          subject: "Newsletter Signup",
          message: "New newsletter signup from website footer. Email: " + emailValue,
        },
        { publicKey: "LyFrQSP0J8xcnoKSH" }
      )
      .then(
        () => {
          setNlStatus({
            type: "success",
            message: "You're subscribed! Watch your inbox for deals & tips.",
          });
          nlForm.current.reset();
          setNlSubmitting(false);
        },
        () => {
          setNlStatus({
            type: "error",
            message: "Something went wrong. Please try again.",
          });
          setNlSubmitting(false);
        }
      );
  };

  return (
    <>
      {/* ============================ PREMIUM FOOTER ============================ */}
      <footer className="cs-footer">
        <div className="container">
          <div className="cs-footer__grid">
            {/* Brand */}
            <div>
              <img
                className="cs-footer__brand-logo"
                src={process.env.PUBLIC_URL + "/assets/images/logoFooter.png"}
                alt="Cell Station Logo"
                loading="lazy"
              />
              <p className="cs-footer__about">
                Your trusted local phone and computer repair shop since 2006.
                Fast, reliable service with quality parts and warranty included.
              </p>

              <div className="cs-chips">
                <span className="cs-chip">
                  <i className="fas fa-shield-alt" /> Warranty
                </span>
                <span className="cs-chip">
                  <i className="fas fa-clock" /> Same Day
                </span>
                <span className="cs-chip">
                  <i className="fas fa-star" /> 4.7 Rated
                </span>
              </div>

              <ul className="cs-socials">
                <li>
                  <a
                    href="https://www.facebook.com/icellstation/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <span className="fab fa-facebook-f" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.tiktok.com/@icellstation?lang=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="TikTok"
                  >
                    <span className="fab fa-tiktok" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.youtube.com/channel/UC_ju-JULprEqMmO3Wlj7DyA"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                  >
                    <span className="fab fa-youtube" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="cs-col__title">Quick Links</h4>
              <ul className="cs-links">
                {QUICK_LINKS.map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} className="initiate-scripts">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas */}
            <div>
              <h4 className="cs-col__title">Areas We Serve</h4>
              <ul className="cs-links">
                {AREAS.map(([slug, label]) => (
                  <li key={slug}>
                    <Link to={`/areas/${slug}`} className="initiate-scripts">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h4 className="cs-col__title">Policies</h4>
              <ul className="cs-links">
                {POLICIES.map(([to, label]) => (
                  <li key={to}>
                    <Link to={to} className="initiate-scripts">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="cs-col__title">Get in Touch</h4>
              <div className="cs-contact-item">
                <i className="fas fa-phone-alt" />
                <a href="tel:+12159393933">(215) 939-3933</a>
              </div>
              <div className="cs-contact-item">
                <i className="fas fa-envelope" />
                <a href="mailto:icellstation1@gmail.com">icellstation1@gmail.com</a>
              </div>
              <div className="cs-contact-item">
                <i className="fas fa-map-marker-alt" />
                <Link to="/store-location">
                  2500 W Moreland Rd, Space #1077
                  <br />
                  Willow Grove, PA 19090
                </Link>
              </div>

              <h4 className="cs-col__title" style={{ marginTop: "26px" }}>
                Store Hours
              </h4>
              <div className="cs-hours">
                <div className="cs-hours__row">
                  <span>Mon – Thu</span>
                  <span>10AM – 8PM</span>
                </div>
                <div className="cs-hours__row">
                  <span>Fri – Sat</span>
                  <span>10AM – 9PM</span>
                </div>
                <div className="cs-hours__row">
                  <span>Sunday</span>
                  <span>10AM – 8PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter — glass panel */}
          <div className="cs-newsletter">
            <div className="cs-newsletter__copy">
              <h4>
                <i className="fas fa-envelope-open-text" />
                Get Exclusive Deals &amp; Repair Tips
              </h4>
              <p>Subscribe for special offers, tech tips, and seasonal promotions.</p>
            </div>
            <form
              ref={nlForm}
              onSubmit={handleNewsletterSubmit}
              className="cs-newsletter__form"
            >
              <input type="hidden" name="name" value="Newsletter Subscriber" />
              <input type="hidden" name="subject" value="Newsletter Signup" />
              <input
                type="email"
                name="email"
                required
                placeholder="Enter your email address"
              />
              <button type="submit" disabled={nlSubmitting} className="cs-newsletter__btn">
                {nlSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Subscribing…
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane" /> Subscribe
                  </>
                )}
              </button>
              {nlStatus.message && (
                <div className={`cs-nl-status ${nlStatus.type === "success" ? "ok" : "err"}`}>
                  <i
                    className={`fas ${
                      nlStatus.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"
                    } mr-2`}
                  />
                  {nlStatus.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </footer>

      <div className="cs-footer__bottom">
        <div className="container">
          Copyright <b>&copy;</b> 2026 by <b>Cell Station.</b> All rights reserved.
          <ReviewCertificate />
        </div>
      </div>

      {/* ==================== MOBILE GLASS BOTTOM NAV ==================== */}
      <nav
        className={`cs-bottomnav${navReady ? " is-ready" : ""}${
          navCollapsed ? " is-collapsed" : ""
        }`}
        aria-label="Quick actions"
      >
        <div className="cs-bottomnav__items">
          <a
            href="tel:+12159393933"
            className="cs-nav-item"
            onClick={() => trackPhoneCall("mobile_bottom_nav")}
          >
            <i className="fas fa-phone-alt" />
            Call
          </a>
          <Link to="/services" className="cs-nav-item">
            <i className="fas fa-tools" />
            Services
          </Link>
          <Link to="/quote" className="cs-nav-item is-primary">
            <i className="fas fa-bolt" />
            Quote
          </Link>
          <a
            href="https://wa.me/12159393933?text=Hi%20Cell%20Station!%20I%20need%20help%20with%20a%20repair."
            target="_blank"
            rel="noopener noreferrer"
            className="cs-nav-item is-whatsapp"
            onClick={() => trackWhatsApp("mobile_bottom_nav")}
          >
            <i className="fab fa-whatsapp" />
            Chat
          </a>
          <Link to="/vip" className="cs-nav-item">
            <i className="fas fa-crown" />
            VIP+
          </Link>
        </div>

        {/* Collapsed circle — tapping it opens the quote flow */}
        <Link to="/quote" className="cs-bottomnav__fab" aria-label="Get a quote">
          <i className="fas fa-bolt" />
        </Link>
      </nav>

      {/* WhatsApp Floating Button (desktop) */}
      <a
        href="https://wa.me/12159393933?text=Hi%20Cell%20Station!%20I%20need%20help%20with%20a%20repair."
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        onClick={() => trackWhatsApp("float_button")}
      >
        <i className="fab fa-whatsapp" />
        <span className="tooltip-text">Chat with us!</span>
      </a>
    </>
  );
};

export default Footer;
